import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import {
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getMyServiceOrders,
} from "../api/freelancerServices";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
    revision_requested: { className: "text-bg-warning", label: isAr ? "طلب مراجعة" : "Revision Requested" },
    cancellation_requested: { className: "text-bg-warning", label: isAr ? "طلب إلغاء" : "Cancellation Requested" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

export default function MyServiceOrders() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await getMyServiceOrders();

        if (active) {
          setOrders(data);
        }
      } catch (err) {
        logger.error("My service orders error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل طلباتك." : "Unable to load your service orders.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [isAr]);

  return (
    <div className="container py-4 py-lg-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="fw-bold mb-1">{isAr ? "طلباتي للخدمات" : "My Service Orders"}</h1>
          <p className="text-muted mb-0">
            {isAr
              ? "تابع حالة التنفيذ والدفع والضمان والتقييم لكل خدمة اشتريتها."
              : "Track delivery, payment, escrow, and review status for every freelancer service you purchased."}
          </p>
        </div>
        <Link to="/freelancers" className="btn btn-outline-dark rounded-pill px-4">
          {isAr ? "تصفح الخدمات" : "Browse Services"}
        </Link>
      </div>

      {loading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary mb-3" role="status" />
          <p className="mb-0">{isAr ? "جارِ تحميل طلباتك..." : "Loading your orders..."}</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger rounded-4">{error}</div>
      ) : orders.length === 0 ? (
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body py-5 text-center">
            <h2 className="h4 fw-bold mb-2">
              {isAr ? "لا توجد طلبات خدمات بعد" : "You do not have any service orders yet"}
            </h2>
            <p className="text-muted mb-4">
              {isAr
                ? "ابدأ من سوق المستقلين واختر الخدمة المناسبة لك."
                : "Start from the freelancer marketplace and choose the service that fits you."}
            </p>
            <Link to="/freelancers" className="btn btn-primary rounded-pill px-4">
              {isAr ? "عرض الخدمات" : "View Services"}
            </Link>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {orders.map((order) => {
            const service = order.service || {};
            const statusMeta = getStatusMeta(order.status, isAr);

            return (
              <div className="col-12" key={order.id}>
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                  <div className="card-body p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3">
                      <div className="d-flex gap-3">
                        <img
                          src={
                            service?.cover_image ||
                            "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
                          }
                          alt={getFreelancerServiceTitle(service, isAr)}
                          className="course-my-card__image"
                          style={{ width: 120, height: 120, borderRadius: 18 }}
                        />
                        <div>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                            <span
                              className={`badge ${
                                order.payment_status === "paid"
                                  ? "text-bg-success"
                                  : order.payment_status === "refunded"
                                    ? "text-bg-danger"
                                    : "text-bg-warning"
                              }`}
                            >
                              {order.payment_status}
                            </span>
                            {order.payment_summary?.escrow_status ? (
                              <span className="badge text-bg-light">
                                {isAr ? "الضمان" : "Escrow"}: {order.payment_summary.escrow_status}
                              </span>
                            ) : null}
                          </div>

                          <h2 className="h4 fw-bold mb-2">
                            {order.title || getFreelancerServiceTitle(service, isAr)}
                          </h2>
                          <div className="small text-muted d-grid gap-2">
                            <div>
                              {isAr ? "السعر:" : "Price:"}{" "}
                              {formatServiceCurrency(order.price || 0, order.currency || "USD")}
                            </div>
                            <div>
                              {isAr ? "المستقل:" : "Freelancer:"} {order.freelancer?.name || "-"}
                            </div>
                            {order.payment_summary ? (
                              <div>
                                {isAr ? "المسترد:" : "Refunded:"}{" "}
                                {formatServiceCurrency(
                                  order.payment_summary.refund_total || 0,
                                  order.payment_summary.currency || order.currency || "USD"
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex flex-column align-items-start align-items-lg-end gap-2">
                        <button
                          type="button"
                          className="btn btn-outline-dark rounded-pill px-4"
                          onClick={() => navigate(`/service-orders/${order.id}`)}
                        >
                          {isAr ? "عرض التفاصيل" : "Show Details"}
                        </button>
                        <Link
                          to={`/freelancers/${service?.slug || service?.id}`}
                          className="btn btn-primary rounded-pill px-4"
                        >
                          {isAr ? "تفاصيل الخدمة" : "Service Details"}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
